import { FC } from "react";
import { RemoteRegion } from "data/remoteRegions/remoteRegions";
import { add } from "date-fns";
import { JOB_POST_PUBLISH_DURATION_IN_DAYS } from "@config/config.client";
import {
  Compensation,
  JobPost,
  Location,
  Remote,
  Extras,
  JobPostRemoteType,
  Organisation,
} from "@prisma/client";
import { AssignedTag, NewTag, TagType } from "types/tags";

interface Props {
  jobPost: JobPost & {
    compensation: Compensation;
    hiringOrganisation?: Organisation;
    sourcingOrganisation?: Organisation;
    locations: Location[];
    remote: Remote & { regionsPopulated: RemoteRegion[] };
    extras: Extras;
    tags?: (AssignedTag & NewTag)[];
  };
}

export const JobPostingJsonLd: FC<Props> = ({ jobPost }) => {
  // Return early if no jobPost.
  if (!jobPost) return null;

  // EmploymentType from assigned tags.
  const employmentType = jobPost.tags
    ?.filter((t) => t.type === TagType.EMPLOYMENT_TYPE)
    .map((t) => t.id);

  // hiringOrganization !! This one is using american english, so it is spelled with a Z.
  const hiringOrganization = (jobPost?.hiringOrganisation?.name ||
    jobPost?.hiringOrganisation?.webUrl) && {
    "@type": "Organization",
    name:
      jobPost?.hiringOrganisation?.name ||
      jobPost?.hiringOrganisation?.webUrl?.replace("https://", ""),
  };

  // jobLocation
  const firstLocationFormattedAddress =
    jobPost?.locations?.[0]?.formattedAddress ||
    jobPost?.locations?.[0]?.address;
  const jobLocation = firstLocationFormattedAddress && {
    "@type": "Place",
    address: firstLocationFormattedAddress,
  };

  // jobLocationType (TELECOMMUTE)
  const hasRemote =
    jobPost?.remote?.type === JobPostRemoteType.WORLDWIDE ||
    (jobPost?.remote?.type === JobPostRemoteType.SELECTED_REGIONS &&
      jobPost?.remote?.regionsPopulated?.length > 0);
  const jobLocationType = hasRemote ? "TELECOMMUTE" : undefined;

  // baseSalary
  const baseSalary = jobPost?.compensation?.max && {
    "@type": "MonetaryAmount",
    currency: jobPost?.compensation?.currency,
    value: {
      "@type": "QuantitativeValue",
      value: jobPost?.compensation?.max,
      unitText: jobPost?.compensation?.unit,
    },
  };

  // Put it all together.
  const jsonLD = {
    "@context": "http://schema.org",
    "@type": "JobPosting",
    datePosted: jobPost?.createdAt,
    validThrough: add(new Date(jobPost.createdAt), {
      days: JOB_POST_PUBLISH_DURATION_IN_DAYS,
    }),
    title: jobPost?.title,
    description: jobPost?.description,
    ...(employmentType ? { employmentType } : {}),
    ...(hiringOrganization ? { hiringOrganization } : {}),
    ...(jobLocation ? { jobLocation } : {}),
    ...(jobLocationType ? { jobLocationType } : {}),
    ...(baseSalary ? { baseSalary } : {}),
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(jsonLD),
      }}
    />
  );
};
